import styled from "@xstyled/styled-components";
import { tokens } from "../utils/theme";

export const SidebarContainer = styled.aside`
  width: 240px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'radial-gradient(circle at top, rgba(130, 170, 255, 0.16), #050710)'
    : 'radial-gradient(circle at top, rgba(22,163,74,0.10), rgba(255,255,255,0.86))'};
  border-right: 1px solid ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[500] : 'rgba(15, 23, 42, 0.14)'};
  padding: 24px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    flex-direction: row;
    gap: 16px;
    overflow-x: auto;
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  
  @media (max-width: 900px) {
    min-width: 220px;
    gap: 10px;
  }
`;

export const SidebarBrand = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

export const BrandLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const BrandLogo = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 10px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'radial-gradient(circle at 25% 0, #82aaff, #151827)'
    : 'rgba(255,255,255,0.84)'};
  border: ${props => props.theme.palette.mode === 'dark' ? 'none' : '1px solid rgba(15,118,110,0.28)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.theme.palette.mode === 'dark' ? '#050713' : '#0f766e'};
  box-shadow: ${props => props.theme.palette.mode === 'dark' ? '0 0 18px rgba(130, 170, 255, 0.9)' : '0 10px 24px rgba(15,118,110,0.12)'};
  transform: translateY(-24px);
`;

export const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const BrandName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

export const BrandSub = styled.div`
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

export const AccountBadge = styled.div`
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.7)' : 'rgba(15,118,110,0.38)'};
  color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).blueAccent[500] : '#0b3b35'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(5, 16, 18, 0.95)' : 'linear-gradient(90deg, rgba(15,118,110,0.14), rgba(255,255,255,0))'};
  box-shadow: ${props => props.theme.palette.mode === 'dark' ? 'none' : '0 10px 24px rgba(15,118,110,0.10)'};
  margin-top: 18px;
  align-self: flex-start;
  width: fit-content;
`;

export const SidebarSummary = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(5, 9, 18, 0.95)' : 'rgba(255,255,255,0.72)'};
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? '#22273a' : 'rgba(15, 23, 42, 0.14)'};
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  line-height: 1.4;
`;

export const SummaryValue = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${props => {
    if (props.positive) return tokens(props.theme.palette.mode).greenAccent[500];
    if (props.negative) return tokens(props.theme.palette.mode).redAccent[500];
    return tokens(props.theme.palette.mode).grey[100];
  }};
`;

export const SidebarSectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  margin-top: 10px;
  margin-bottom: 12px;
`;

export const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const NavButton = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 9px;
  border-radius: 10px;
  font-size: 13px;
  color: ${props => props.active ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[400]};
  cursor: pointer;
  transition: background 0.18s ease-out, color 0.18s ease-out, transform 0.18s ease-out;
  background: ${props => props.active
    ? (props.theme.palette.mode === 'dark' ? 'linear-gradient(90deg, rgba(130, 170, 255, 0.2), rgba(10, 21, 40, 0.97))' : 'linear-gradient(90deg, rgba(15,118,110,0.12), rgba(255,255,255,0.75))')
    : 'transparent'};
  transform: ${props => props.active ? 'translateX(2px)' : 'none'};

  &:hover {
    background: ${props => !props.active && (props.theme.palette.mode === 'dark' ? 'rgba(12, 16, 30, 0.95)' : 'rgba(15, 23, 42, 0.05)')};
    color: ${props => !props.active && tokens(props.theme.palette.mode).grey[100]};
  }
`;

export const NavIcon = styled.span`
  width: 18px;
  text-align: center;
  font-size: 14px;
  opacity: 0.9;
`;

export const SidebarFooter = styled.div`
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(32, 37, 58, 0.9)' : 'rgba(15, 23, 42, 0.14)'};
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (max-width: 900px) {
    display: none;
  }
`;
